import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowLeft, ChevronDown, ChevronRight, ChevronUp, Download, Loader2 } from 'lucide-react';
import CircleTimer from './CircleTimer';
import {
  AHREFS_ATTRIBUTION_URL,
  AHREFS_DR_HELP,
  formatAhrefsDr,
  isAhrefsDrLoading,
  normalizeDomainHost
} from '../utils/linkIntelligenceScores';

const formatCount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return n.toLocaleString('en-GB');
};

const AhrefsDrHeader = () => (
  <a
    href={AHREFS_ATTRIBUTION_URL}
    target="_blank"
    rel="noreferrer"
    className="hover:text-primary-600 hover:underline"
    onClick={(e) => e.stopPropagation()}
    title={AHREFS_DR_HELP}
  >
    Domain Rating by Ahrefs
  </a>
);

const AhrefsDrCell = ({ competitor, row }) => {
  if (isAhrefsDrLoading(competitor, row)) {
    return <CircleTimer />;
  }
  return (
    <span className="font-bold text-indigo-700" title={AHREFS_DR_HELP}>
      {formatAhrefsDr(row.ahrefs_dr)}
    </span>
  );
};

const SORTABLE_COLUMNS = {
  ahrefs_dr: (row) => Number(row.ahrefs_dr) || -1,
  rank_value: (row) => Number(row.rank_value) || 0,
  organic_traffic: (row) => Number(row.organic_traffic) || 0,
  backlinks: (row) => Number(row.backlinks) || 0,
  spam_score: (row) => Number(row.spam_score) || 0
};

const SortableHeader = ({ columnKey, label, title, sortKey, sortDir, onSort }) => {
  const active = sortKey === columnKey;
  return (
    <th
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:bg-gray-100 group"
      onClick={() => onSort(columnKey)}
      title={title || 'Click to sort'}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          sortDir === 'desc' ? (
            <ChevronDown className="h-3.5 w-3.5 text-primary-600" />
          ) : (
            <ChevronUp className="h-3.5 w-3.5 text-primary-600" />
          )
        ) : (
          <span className="inline-flex flex-col opacity-0 group-hover:opacity-40 -space-y-1">
            <ChevronUp className="h-2.5 w-2.5" />
            <ChevronDown className="h-2.5 w-2.5" />
          </span>
        )}
      </span>
    </th>
  );
};

const pagesEmptyMessage = (row, loaded) => {
  const storedPages = Number(row.referring_pages) || 0;
  const apiTotal = Number(loaded?.totalCount) || 0;
  if (storedPages > 0 || apiTotal > 0) {
    const count = storedPages || apiTotal;
    return `DataForSEO reports ${count.toLocaleString('en-GB')} referring page(s) from this domain, but no individual page URLs were returned in this lookup (common when links are indexed under www or a subdomain variant).`;
  }
  return 'No referring pages indexed from this domain to the competitor in DataForSEO.';
};

const HistoryTrendChart = ({ history }) => {
  const points = useMemo(() => {
    if (!Array.isArray(history) || history.length === 0) return [];
    return history.map((row) => ({
      label: String(row.snapshot_date || '').slice(0, 7),
      value: Number(row.referring_domains) || 0
    }));
  }, [history]);

  if (points.length === 0) {
    return <p className="text-sm text-gray-500">No history data yet. Re-fetch to load trend.</p>;
  }

  const width = 520;
  const height = 160;
  const pad = 24;
  const maxVal = Math.max(...points.map((p) => p.value), 1);
  const step = points.length > 1 ? (width - pad * 2) / (points.length - 1) : 0;
  const coords = points.map((p, i) => {
    const x = pad + step * i;
    const y = height - pad - (p.value / maxVal) * (height - pad * 2);
    return { x, y, ...p };
  });
  const polyline = coords.map((c) => `${c.x},${c.y}`).join(' ');

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-xl h-40">
        <polyline
          fill="none"
          stroke="#2563eb"
          strokeWidth="2.5"
          points={polyline}
        />
        {coords.map((c) => (
          <g key={c.label}>
            <circle cx={c.x} cy={c.y} r="3.5" fill="#2563eb" />
            <text x={c.x} y={height - 4} textAnchor="middle" fontSize="9" fill="#6b7280">
              {c.label}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const TldBreakdown = ({ breakdown }) => {
  if (!Array.isArray(breakdown) || breakdown.length === 0) {
    return <p className="text-sm text-gray-500">No TLD data available.</p>;
  }
  const top = breakdown.slice(0, 8);
  const max = top[0]?.count || 1;
  return (
    <div className="space-y-2">
      {top.map((row) => (
        <div key={row.tld}>
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>{row.tld}</span>
            <span>
              {row.count} ({row.percent}%)
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full"
              style={{ width: `${Math.max(4, (row.count / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const getLinkGapRowClass = (row) => (row.links_to_client ? 'bg-green-50' : '');

const ReferringDomainsReport = ({ projectId, competitorId, data }) => {
  const { competitor, domains, history, tld_breakdown: tldBreakdown, link_gap_count: linkGapCount } = data;
  const clientDomain = normalizeDomainHost(
    competitor.client_domain || competitor.client_website_url || ''
  ) || null;
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [pagesByDomain, setPagesByDomain] = useState({});
  const [loadingPagesFor, setLoadingPagesFor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const sortedDomains = useMemo(() => {
    const list = [...domains];
    if (!sortKey || !SORTABLE_COLUMNS[sortKey]) {
      return list.sort((a, b) => (Number(a.position_index) || 0) - (Number(b.position_index) || 0));
    }
    const getValue = SORTABLE_COLUMNS[sortKey];
    return list.sort((a, b) => {
      const diff = getValue(a) - getValue(b);
      if (diff !== 0) {
        return sortDir === 'asc' ? diff : -diff;
      }
      return String(a.domain || '').localeCompare(String(b.domain || ''));
    });
  }, [domains, sortKey, sortDir]);

  const handleSort = (columnKey) => {
    if (sortKey === columnKey) {
      setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
      return;
    }
    setSortKey(columnKey);
    setSortDir('desc');
  };

  const overlapCount =
    linkGapCount ?? domains.filter((row) => row.links_to_client).length;
  const withTraffic = domains.filter((row) => Number(row.organic_traffic) > 0).length;
  const avgDomainRank = domains.length
    ? Math.round(domains.reduce((sum, row) => sum + Number(row.rank_value || 0), 0) / domains.length)
    : 0;

  const toggleDomainExpand = async (domain) => {
    if (expandedDomain === domain) {
      setExpandedDomain(null);
      return;
    }
    setExpandedDomain(domain);
    if (pagesByDomain[domain]?.pages?.length > 0) return;
    setLoadingPagesFor(domain);
    try {
      const response = await axios.get(
        `/api/link-intelligence/competitors/${competitorId}/referring-pages`,
        { params: { domain } }
      );
      setPagesByDomain((prev) => ({
        ...prev,
        [domain]: {
          pages: response.data?.pages || [],
          totalCount: response.data?.total_count ?? 0
        }
      }));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load referring pages');
      setPagesByDomain((prev) => ({ ...prev, [domain]: { pages: [], totalCount: 0 } }));
    } finally {
      setLoadingPagesFor(null);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/link-intelligence/competitors/${competitorId}/referring-domains/export`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!response.ok) {
        throw new Error('Export failed');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `referring-domains-${competitor.domain}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Referring Domains</h1>
          <p className="text-gray-600">
            {competitor.client_name} · {competitor.domain} · top {domains.length} domains
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-secondary inline-flex items-center gap-2"
            onClick={exportCsv}
            disabled={exporting || domains.length === 0}
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </button>
          <Link
            to={`/link-intelligence/projects/${projectId}`}
            className="btn btn-secondary inline-flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Project
          </Link>
        </div>
      </div>

      <div className="card bg-primary-50 border-primary-100">
        <h2 className="text-sm font-semibold text-primary-900 mb-2">
          Competitor profile (DataForSEO metrics + Ahrefs DR)
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
          <div title={AHREFS_DR_HELP}>
            <p className="text-xs uppercase text-gray-500">
              <AhrefsDrHeader />
            </p>
            <p className="text-xl font-bold text-primary-700">
              {isAhrefsDrLoading(competitor, { ahrefs_dr: competitor.target_ahrefs_dr }) ? (
                <CircleTimer size={28} />
              ) : (
                formatAhrefsDr(competitor.target_ahrefs_dr)
              )}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Domain rank</p>
            <p className="text-xl font-bold text-primary-700">{formatCount(competitor.target_rank)}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Backlinks</p>
            <p className="text-xl font-bold text-primary-700">{formatCount(competitor.target_backlinks)}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Referring domains</p>
            <p className="text-xl font-bold text-primary-700">{formatCount(competitor.target_referring_domains)}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Main domains</p>
            <p className="text-xl font-bold text-primary-700">{formatCount(competitor.target_referring_main_domains)}</p>
          </div>
          <div>
            <p className="text-xs uppercase text-gray-500">Spam score</p>
            <p className="text-xl font-bold text-primary-700">{formatCount(competitor.target_spam_score)}</p>
          </div>
        </div>
      </div>

      {competitor.ahrefs_dr_status === 'fetching' && (
        <div className="card border-indigo-100 bg-indigo-50/50 flex flex-wrap items-center gap-3 text-sm text-indigo-900">
          <CircleTimer size={24} />
          <span>
            Loading Domain Rating from Ahrefs — {competitor.ahrefs_dr_fetched ?? 0} of{' '}
            {competitor.ahrefs_dr_total ?? '…'} domains (~4 min for 200 domains).
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Referring domains trend (12 months)</h2>
          <HistoryTrendChart history={history} />
        </div>
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Referring TLDs</h2>
          <TldBreakdown breakdown={tldBreakdown} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card text-center">
          <p className="text-xs uppercase text-gray-500">Stored referring domains</p>
          <p className="text-3xl font-bold text-primary-600 mt-1">{formatCount(domains.length)}</p>
        </div>
        <div className="card text-center border-green-100 bg-green-50/40">
          <p className="text-xs uppercase text-gray-500">Link overlap (client + competitor)</p>
          <p className="text-3xl font-bold text-green-700 mt-1">{formatCount(overlapCount)}</p>
          {clientDomain && (
            <p className="text-xs text-gray-500 mt-1 truncate" title={clientDomain}>
              vs {clientDomain}
            </p>
          )}
        </div>
        <div className="card text-center">
          <p className="text-xs uppercase text-gray-500">Avg referring domain rank</p>
          <p className="text-3xl font-bold text-primary-600 mt-1">{formatCount(avgDomainRank)}</p>
        </div>
        <div className="card text-center">
          <p className="text-xs uppercase text-gray-500">With organic traffic (ETV)</p>
          <p className="text-3xl font-bold text-primary-600 mt-1">{formatCount(withTraffic)}</p>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <p className="text-sm text-gray-600">
            Click a row to expand top referring pages from that domain (live from Backlinks API).
            Click column headers to sort by Domain Rating, Domain Rank, Organic Traffic, Backlinks, or Spam.
          </p>
          <div className="flex items-center gap-4 text-sm text-gray-700 shrink-0">
            <span className="font-semibold">Link Gap:</span>
            <span className="inline-flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-sm bg-green-50 border border-green-200" />
              Also links to client
            </span>
          </div>
        </div>
        {!clientDomain && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-3">
            Add a website URL on the client record and re-fetch to enable Link Gap highlighting.
          </p>
        )}
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-8" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain</th>
              <SortableHeader
                columnKey="ahrefs_dr"
                label={<AhrefsDrHeader />}
                title={AHREFS_DR_HELP}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                columnKey="rank_value"
                label="Domain Rank"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                columnKey="organic_traffic"
                label="Organic Traffic"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                columnKey="backlinks"
                label="Backlinks"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortableHeader
                columnKey="spam_score"
                label="Spam"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
              />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {domains.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                  No referring domains stored. Re-fetch from the project page.
                </td>
              </tr>
            ) : (
              sortedDomains.map((row, index) => {
                const isOpen = expandedDomain === row.domain;
                const loaded = pagesByDomain[row.domain];
                const pages = loaded?.pages;
                const loadingPages = loadingPagesFor === row.domain;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`cursor-pointer hover:bg-gray-50 ${getLinkGapRowClass(row)}`}
                      onClick={() => toggleDomainExpand(row.domain)}
                    >
                      <td className="px-4 py-3">
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        <span className="text-gray-400 mr-2">{index + 1}.</span>
                        {row.domain}
                        {Number(row.referring_pages) > 0 && (
                          <span className="ml-2 text-xs text-gray-400">
                            ({formatCount(row.referring_pages)} pages)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <AhrefsDrCell competitor={competitor} row={row} />
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-800">{formatCount(row.rank_value)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-primary-700">
                        {formatCount(row.organic_traffic)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatCount(row.backlinks)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.spam_score}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50">
                        <td colSpan={7} className="px-4 pb-4 pt-0">
                          {loadingPages ? (
                            <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Loading referring pages…
                            </div>
                          ) : !pages || pages.length === 0 ? (
                            <p className="py-3 text-sm text-gray-500">{pagesEmptyMessage(row, loaded)}</p>
                          ) : (
                            <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white mt-2">
                              <table className="min-w-full text-sm">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Referring page</th>
                                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Target URL</th>
                                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Anchor</th>
                                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Rank</th>
                                    <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">DF</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pages.map((page, pageIdx) => (
                                    <tr key={`${row.domain}-${pageIdx}`} className="border-t border-gray-100">
                                      <td className="px-3 py-2 max-w-xs truncate" title={page.url_from}>
                                        {page.url_from ? (
                                          <a
                                            href={page.url_from}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-primary-600 hover:underline"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            View
                                          </a>
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                      <td className="px-3 py-2 max-w-xs truncate" title={page.url_to}>
                                        {page.url_to ? (
                                          <a
                                            href={page.url_to}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-primary-600 hover:underline"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            View
                                          </a>
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                      <td className="px-3 py-2 max-w-[12rem] truncate" title={page.anchor}>
                                        {page.anchor || '—'}
                                      </td>
                                      <td className="px-3 py-2">{formatCount(page.rank)}</td>
                                      <td className="px-3 py-2">{page.dofollow ? 'Yes' : 'No'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-gray-500">
          Green rows link to both the competitor and the client site (DataForSEO domain intersection).
          {' '}
          <a href={AHREFS_ATTRIBUTION_URL} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">
            Domain Rating by Ahrefs
          </a>
          {' '}
          is fetched via the Ahrefs free API (~50/min). Re-fetch competitor data to refresh metrics.
        </p>
      </div>
    </div>
  );
};

export default ReferringDomainsReport;
